import React, { useState, useCallback } from "react";
import { observer } from "mobx-react";
import styled, { css } from "styled-components";
import { useStore } from "store";
import Loading from "../../../../components/Loading";
import { RenderCoupon } from "..";
import { transformImageTights } from "utils/image";
import { CouponType } from "services/page/admin";
import { ReceivedCouponStatusEnum } from "utils/zeusFetch";
import { getReceivedCouponIcon } from "views/utils/receiveCoupon";
import { px2rem } from "utils/browser/resizer";

interface IContainerProps {
	columnSize: number;
}

const Container = styled.div`
	position: relative;
	font-size: 0;
	.receive-mask {
		width: 100%;
		height: 100%;
		position: absolute;
		background-color: rgba(123, 123, 123, 0.3);
		font-size: 0;
		top: 0;
		left: 0;
		img {
			position: absolute;
			${(props: IContainerProps) => {
				if (props.columnSize === 1) {
					return css`
						top: ${px2rem(6)};
						right: ${px2rem(5)};
					`;
				}
				if (props.columnSize === 2) {
					return css`
						top: ${px2rem(7)};
						right: ${px2rem(7)};
					`;
				}
				if (props.columnSize === 3) {
					return css`
						top: ${px2rem(5)};
						right: ${px2rem(6)};
					`;
				}
			}}
			height: unset;
			width: ${px2rem(60)};
			height: ${px2rem(60)};
		}
	}
`;

export interface CouponBtnProps {
	columnSize: number;
	language: string;
	coupon: RenderCoupon;
	handleClick: (code: string, index: number) => Promise<void>;
	index?: number;
	type: CouponType;
}

function useReceiveCoupon(coupon: RenderCoupon, type: CouponType) {
	const { receiveCoupon } = useStore();
	const [isLoading, setIsLoading] = useState(false);
	
	const hasReceived =
		type === CouponType.ReceiveCoupon &&
		receiveCoupon.getCouponInfoByCampaignKey(coupon.code)?.status ===
			ReceivedCouponStatusEnum.OVERSTEP;

	return {
		hasReceived,
		isLoading,
		setIsLoading,
		updateCouponInfo: receiveCoupon.updateCouponInfo,
	};
}

export const CouponBtn = observer((props: CouponBtnProps) => {
	const { coupon, index = 0, handleClick, type, language, columnSize } = props;

	const src = transformImageTights(coupon?.img);
	const { hasReceived, isLoading, setIsLoading, updateCouponInfo } = useReceiveCoupon(coupon, type);

	const onClick = useCallback(async () => {
		if (type === CouponType.CopyCoupon) {
			return updateCouponInfo(coupon?.code);
		}
		if (isLoading) return;
		try {
			setIsLoading(true);
			await handleClick(coupon?.code, index);
			await updateCouponInfo(coupon?.code);
		} finally {
			setIsLoading(false);
		}
	}, [handleClick, coupon, setIsLoading, updateCouponInfo]);
	
	return (
		<Container onClick={onClick} columnSize={columnSize}>
			<img src={src} alt="couponBtn" />
			{isLoading ? <Loading  iconStyle={{ width: 30, height: 30 }} /> : null}
			{hasReceived ? (
				<div className="receive-mask">
					<img
						src={transformImageTights(getReceivedCouponIcon(language))}
						alt="couponReceived"
					/>
				</div>
			) : null}
		</Container>
	);
});
